
import { supabase } from "@/integrations/supabase/client";
import { CalendarNotificationService } from "./notification-integration";

export interface ConnectionHealth {
  connection: any;
  isHealthy: boolean;
  needsReauth: boolean;
  error: string | null;
  lastChecked: Date;
}

export class CalendarConnectionManager {
  private static cache = new Map<string, { health: ConnectionHealth; timestamp: number }>();
  private static readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  static clearCache(): void {
    this.cache.clear();
  }

  static clearUserCache(userId: string): void {
    const cacheKey = `connection_health_${userId}`;
    this.cache.delete(cacheKey);
  }

  static async forceRefreshValidation(userId: string): Promise<ConnectionHealth> {
    // Clear the cache for this user
    this.clearUserCache(userId);
    
    // Force a fresh validation
    return await this.checkConnectionHealth(userId);
  }

  static async testValidation(userId: string): Promise<{
    health: ConnectionHealth;
    debug: {
      cacheSize: number;
      cacheKeys: string[];
    };
  }> {
    console.log(`[CalendarConnectionManager] Testing validation for user ${userId}`);
    
    // Clear cache first
    this.clearUserCache(userId);
    
    // Get fresh validation
    const health = await this.checkConnectionHealth(userId);
    
    // Get debug info
    const cacheKeys = Array.from(this.cache.keys());
    const cacheSize = this.cache.size;
    
    console.log(`[CalendarConnectionManager] Test results:`, {
      userId,
      health,
      cacheSize,
      cacheKeys
    });
    
    return {
      health,
      debug: {
        cacheSize,
        cacheKeys
      }
    };
  }

  static async reactivateMeetingLinks(userId: string): Promise<{ success: boolean; error?: string; reactivatedCount?: number }> {
    try {
      console.log(`[CalendarConnectionManager] Attempting to reactivate meeting links for user ${userId}`);
      
      // First check if the calendar connection is healthy
      const health = await this.checkConnectionHealth(userId);
      
      if (!health.isHealthy) {
        console.log(`[CalendarConnectionManager] Cannot reactivate links - calendar connection unhealthy:`, health.error);
        return { 
          success: false, 
          error: `Calendar connection is unhealthy: ${health.error}` 
        };
      }
      
      // Reactivate all deactivated meeting links
      const { data: updatedLinks, error } = await supabase
        .from('meeting_links')
        .update({ 
          active: true,
          deactivation_reason: null,
          deactivated_at: null
        })
        .eq('user_id', userId)
        .eq('active', false)
        .select('id, name');
      
      if (error) {
        console.error(`[CalendarConnectionManager] Error reactivating meeting links:`, error);
        return { success: false, error: error.message };
      }
      
      console.log(`[CalendarConnectionManager] Successfully reactivated ${updatedLinks?.length || 0} meeting links`);
      
      return { 
        success: true, 
        reactivatedCount: updatedLinks?.length || 0 
      };
    } catch (err) {
      console.error(`[CalendarConnectionManager] Error in reactivateMeetingLinks:`, err);
      return { 
        success: false, 
        error: err instanceof Error ? err.message : 'Unknown error' 
      };
    }
  }

  static async validateConnectionForBooking(userId: string): Promise<boolean> {
    try {
      const health = await this.checkConnectionHealth(userId);
      return health.isHealthy && !health.needsReauth;
    } catch (error) {
      console.error('Error validating connection for booking:', error);
      return false;
    }
  }

  static async checkConnectionHealth(userId?: string): Promise<ConnectionHealth> {
    try {
      let targetUserId = userId;
      
      if (!targetUserId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          return {
            connection: null,
            isHealthy: false,
            needsReauth: true,
            error: "No authenticated user found",
            lastChecked: new Date()
          };
        }
        targetUserId = user.id;
      }

      // Check cache first
      const cacheKey = `connection_health_${targetUserId}`;
      const cached = this.cache.get(cacheKey);
      if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
        console.log(`[CalendarConnectionManager] Using cached health result for user ${targetUserId}`);
        return cached.health;
      }

      console.log(`[CalendarConnectionManager] Checking connection health for user ${targetUserId}`);

      const { data: connection, error } = await supabase
        .from("calendar_connections")
        .select("*")
        .eq("user_id", targetUserId)
        .eq("provider", "google")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("Error checking connection health:", error);
        const healthResult = {
          connection: null,
          isHealthy: false,
          needsReauth: true,
          error: error.message,
          lastChecked: new Date()
        };
        this.cache.set(cacheKey, { health: healthResult, timestamp: Date.now() });
        return healthResult;
      }

      if (!connection) {
        console.log(`[CalendarConnectionManager] No calendar connection found for user ${targetUserId}`);
        const healthResult = {
          connection: null,
          isHealthy: false,
          needsReauth: true,
          error: "No calendar connection found",
          lastChecked: new Date()
        };
        this.cache.set(cacheKey, { health: healthResult, timestamp: Date.now() });
        return healthResult;
      }

      // EXTREMELY LENIENT VALIDATION - Only consider truly catastrophic failures as unhealthy
      
      // Check if token is expired - allow 2 HOURS grace period (was 15 minutes)
      const now = new Date();
      const expiresAt = new Date(connection.expires_at);
      const twoHoursFromNow = new Date(now.getTime() + 2 * 60 * 60 * 1000); // 2 HOURS grace period
      const isExpired = expiresAt <= twoHoursFromNow;

      // Check failure count - only consider 50+ failures as critical (was 20)
      const failureCount = connection.refresh_failure_count || 0;
      const hasTooManyFailures = failureCount >= 50; // 50+ failures is catastrophic

      // Check token health status from database - be very lenient
      const tokenHealthStatus = connection.token_health_status || 'healthy';
      // Only consider 'invalid' as a real problem - 'expired' and 'warning' are fine
      const hasDatabaseHealthIssue = tokenHealthStatus === 'invalid';

      // Connection is healthy if:
      // 1. Token is not expired (with 2-hour grace period)
      // 2. Not too many failures (50+ is the threshold)
      // 3. Database health status is not 'invalid' (only truly invalid connections are unhealthy)
      const isHealthy = !isExpired && !hasTooManyFailures && !hasDatabaseHealthIssue;
      
      // Only need reauth if truly catastrophic failure
      const needsReauth = (expiresAt <= now && failureCount >= 10) || hasTooManyFailures || hasDatabaseHealthIssue;

      console.log(`[CalendarConnectionManager] EXTREMELY LENIENT health check results for user ${targetUserId}:`, {
        connectionId: connection.id,
        email: connection.email,
        expiresAt: connection.expires_at,
        isExpired,
        failureCount,
        hasTooManyFailures,
        tokenHealthStatus,
        hasDatabaseHealthIssue,
        isHealthy,
        needsReauth,
        gracePeriod: '2 hours',
        failureThreshold: '50+ failures'
      });

      const healthResult = {
        connection,
        isHealthy,
        needsReauth,
        error: needsReauth ? (
          expiresAt <= now && failureCount >= 10 ? "Token expired with multiple failures" : 
          hasTooManyFailures ? "Catastrophic failure count" :
          hasDatabaseHealthIssue ? "Invalid connection" :
          null
        ) : null,
        lastChecked: new Date()
      };

      // Cache the result
      this.cache.set(cacheKey, { health: healthResult, timestamp: Date.now() });
      return healthResult;

    } catch (err) {
      console.error("Error in checkConnectionHealth:", err);
      
      // Check if this is a network error (temporary) vs actual calendar issue
      const isNetworkError = err instanceof Error && (
        err.message.includes('Failed to fetch') ||
        err.message.includes('ERR_INTERNET_DISCONNECTED') ||
        err.message.includes('ERR_NETWORK_CHANGED') ||
        err.message.includes('ERR_CONNECTION_REFUSED') ||
        err.message.includes('ERR_TIMED_OUT')
      );
      
      if (isNetworkError) {
        console.log(`🌐 Network error detected, using cached health result if available for user ${targetUserId}`);
        
        // Try to return cached result if available, otherwise return healthy to avoid false alarms
        const cached = this.cache.get(cacheKey);
        if (cached && Date.now() - cached.timestamp < 5 * 60 * 1000) { // 5 minutes
          console.log(`📋 Using cached health result due to network error`);
          return cached.health;
        }
        
        // If no recent cache, assume healthy to avoid false alarms during network issues
        return {
          connection: null,
          isHealthy: true, // Assume healthy during network issues
          needsReauth: false,
          error: null,
          lastChecked: new Date()
        };
      }
      
      // For non-network errors, treat as actual calendar issue
      const healthResult = {
        connection: null,
        isHealthy: false,
        needsReauth: true,
        error: err instanceof Error ? err.message : "Unknown error",
        lastChecked: new Date()
      };
      return healthResult;
    }
  }

  static async cleanupInvalidConnections(): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("No authenticated user found");
      }

      console.log(`🧹 Cleaning up invalid calendar connections for user ${user.id}...`);

      // Remove connections with too many failures or expired tokens
      const { error } = await supabase
        .from("calendar_connections")
        .delete()
        .eq("user_id", user.id)
        .eq("provider", "google")
        .or("refresh_failure_count.gte.3,expires_at.lt.now(),token_health_status.eq.error");

      if (error) {
        console.error("Error cleaning up invalid connections:", error);
        throw error;
      }

      // Clear the cache for this user
      this.clearCache();

      console.log("✅ Invalid calendar connections cleaned up successfully");
    } catch (err) {
      console.error("Error in cleanupInvalidConnections:", err);
      throw err;
    }
  }

  static async notifyHealthIssue(userId: string, email: string, issue: string): Promise<void> {
    // Log health issue but don't send notifications for temporary problems
    console.log(`📋 Calendar health issue logged for ${userId} (${email}): ${issue} - automatic retry will handle this`);
  }
}
